import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { connectBulkWs } from "../lib/ws";
import CommKpiCard from "../components/admin/CommKpiCard";
import UsersTable from "../components/admin/UsersTable";
import ProfessionalProfileModal from "../components/admin/ProfessionalProfileModal";
import CandidaturasList from "../components/admin/CandidaturasList";
import ClinicsTable from "../components/admin/ClinicsTable";
import CommsPanel from "../components/admin/CommsPanel";
import { CommHealthPanel } from "../components/admin/CommHealthPanel";
import LivePlantaoPanel from "../components/admin/LivePlantaoPanel";
import CertAnalyticsPanel from "../components/admin/CertAnalyticsPanel";
import ReopenedCasesPanel from "../components/admin/ReopenedCasesPanel";
import CancelledCasesPanel from "../components/admin/CancelledCasesPanel";
import AdminCouponsPanel from "../components/admin/AdminCouponsPanel";
import AdminConveniosPanel from "../components/admin/AdminConveniosPanel";
import AdminFinancePanel from "../components/admin/AdminFinancePanel";
import ActiveAlertsPanel from "../components/admin/ActiveAlertsPanel";
import ReviewsPanel from "../components/admin/ReviewsPanel";
import ProfessionalsTable from "../components/admin/ProfessionalsTable";
import PixStatusWidget from "../components/admin/PixStatusWidget";
import OutreachCampaign from "../components/admin/OutreachCampaign";
import TemplatesHealthPanel from "../components/admin/TemplatesHealthPanel";
import CommsStatusBanner from "../components/admin/CommsStatusBanner";
import AnalyticsLivePanel from "../components/admin/AnalyticsLivePanel";
import BannedUsersPanel from "../components/admin/BannedUsersPanel";
import CourtesiesPanel from "../components/admin/CourtesiesPanel";
import WhatsAppLiveNotifier from "../components/admin/WhatsAppLiveNotifier";
import useInboundToast from "../components/admin/useInboundToast";
import { Users, Activity, Building2, Shield, ShieldOff, Plus, RotateCcw, Radio, BarChart3, Send, Inbox, AlertTriangle, DollarSign, Siren, Heart, UserCog, Eye, Gift, MessagesSquare, ShieldCheck, HelpCircle } from "lucide-react";

const ROLE_LABEL = {
  familia: "Família", consultor: "Consultor", clinica: "Unidade Parceira", profissional: "Profissional", admin: "Admin",
};
const ROLE_COLORS = {
  familia: "bg-blue-100 text-blue-800", consultor: "bg-indigo-100 text-indigo-800",
  clinica: "bg-amber-100 text-amber-800", admin: "bg-purple-100 text-purple-800",
};

const AdminDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [tab, setTab] = useState("users");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "consultor" });
  const [editingUser, setEditingUser] = useState(null);
  const [migratingProfessional, setMigratingProfessional] = useState(null);
  const [editingClinic, setEditingClinic] = useState(null);
  const [live, setLive] = useState(null);
  const [commHistory, setCommHistory] = useState([]);
  const [certAnalytics, setCertAnalytics] = useState(null);
  const [commKpi, setCommKpi] = useState(null);
  const [bulkLive, setBulkLive] = useState(null); // { bulk_id, invites, winner }

  const load = useCallback(async () => {
    try {
      const [o, u, a, cl, k] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/users"),
        api.get("/admin/applications"),
        api.get("/admin/clinics"),
        api.get("/admin/comm/kpi").catch(() => ({ data: null })),
      ]);
      setOverview(o.data); setUsers(u.data); setApplications(a.data); setClinics(cl.data);
      setCommKpi(k.data);
    } catch { toast.error("Erro ao carregar governança"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Modo Plantão Diretor: polling ao vivo do "quem está de plantão"
  const loadLive = useCallback(async () => {
    try { const r = await api.get("/admin/on-call/live"); setLive(r.data); }
    catch { /* silencioso — polling não deve incomodar */ }
  }, []);
  useEffect(() => {
    if (tab !== "live") return;
    loadLive();
    const t = setInterval(loadLive, 30000);
    return () => clearInterval(t);
  }, [tab, loadLive]);

  const kickOnCall = async (u) => {
    if (!window.confirm(`Tirar ${u.name} do plantão? A RAF tentará escalar automaticamente outro consultor agendado para o horário atual.`)) return;
    try {
      const { data } = await api.post(`/admin/on-call/${u.id}/kick`);
      if (data.covered_by) {
        toast.success(`Plantão coberto por ${data.covered_by.name}${data.covered_by.already_on_call ? " (já estava on-line)" : ""}`);
      } else {
        toast.warning("Consultor removido — não havia colega agendado para cobrir agora");
      }
      loadLive();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const loadCertAnalytics = useCallback(async () => {
    try { const r = await api.get("/admin/certificates/downloads"); setCertAnalytics(r.data); }
    catch { /* silencioso */ }
  }, []);
  useEffect(() => { if (tab === "analytics") loadCertAnalytics(); }, [tab, loadCertAnalytics]);

  const [heatmapFilter, setHeatmapFilter] = useState(null); // { day, hour }
  const loadCommHistory = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ limit: "50" });
      if (heatmapFilter) {
        qs.set("weekday", String(heatmapFilter.day));
        qs.set("hour", String(heatmapFilter.hour));
      }
      const r = await api.get(`/admin/comm/history?${qs.toString()}`);
      setCommHistory(r.data.items || []);
    } catch { /* silencioso */ }
  }, [heatmapFilter]);
  useEffect(() => {
    if (tab !== "sms") return;
    loadCommHistory();
    const t = setInterval(loadCommHistory, 20000);
    return () => clearInterval(t);
  }, [tab, loadCommHistory]);
  const onHeatmapCell = useCallback((day, hour) => {
    setHeatmapFilter({ day, hour });
    setTab("sms");
    toast.info(`Filtrando histórico: ${["Seg","Ter","Qua","Qui","Sex","Sáb","Dom"][day]} ${String(hour).padStart(2,"0")}:00`);
  }, []);

  // WebSocket global do painel admin — recebe inbound WA/SMS em tempo real
  useInboundToast({
    onInboundEvent: (ev) => setCommHistory((prev) => [{ ...ev }, ...prev].slice(0, 20)),
    onShowHistory: useCallback(() => { setTab("sms"); loadCommHistory(); }, [loadCommHistory]),
  });

  // Bulk invite realtime — abre WS quando bulkLive é setado
  useEffect(() => {
    if (!bulkLive?.bulk_id) return;
    const conn = connectBulkWs(bulkLive.bulk_id, {
      onEvent: (ev) => {
        if (ev.type === "invite_accepted") {
          setBulkLive((prev) => prev ? { ...prev, winner: { id: ev.target_id, name: ev.target_name, at: ev.at } } : prev);
          toast.success(`✅ ${ev.target_name} aceitou o plantão!`);
        }
      },
    });
    return () => conn.close();
  }, [bulkLive?.bulk_id]);

  // Aggregate counts que aparecem em várias abas — evita re-scanear applications a cada render.
  const pendingApplicationsCount = useMemo(
    () => applications.filter((a) => a.status === "pending").length,
    [applications],
  );

  const resetCases = async () => {
    const text = window.prompt("Isso deletará TODOS os casos, mensagens, documentos, planos, propostas, candidaturas e auditoria. Users e Unidades Parceiras são preservados.\n\nDigite CONFIRMO para continuar:");
    if (text !== "CONFIRMO") { toast.error("Cancelado"); return; }
    try {
      const { data } = await api.post("/admin/reset-cases?confirm=CONFIRMO");
      const deleted = Object.entries(data.deleted).map(([k, v]) => `${k}: ${v}`).join("\n");
      window.alert(`Sistema limpo com sucesso.\n\n${deleted}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const decide = async (id, decision) => {
    const notes = decision === "reject"
      ? window.prompt("Motivo da rejeição (opcional):", "") ?? ""
      : "";
    if (decision === "reject" && !window.confirm("Confirmar rejeição da candidatura?")) return;
    try {
      const { data } = await api.post(`/admin/applications/${id}/decide`, { decision, notes });
      if (decision === "approve" && data.temp_password) {
        const channels = [];
        if (data.email_sent) channels.push("e-mail");
        if (data.whatsapp_sent) channels.push("WhatsApp");
        const channelMsg = channels.length > 0
          ? `Enviamos automaticamente por ${channels.join(" + ")} para o candidato.`
          : `⚠️ Envio automático falhou — repasse manualmente pelo WhatsApp abaixo.`;
        window.alert(
          `✅ Candidatura APROVADA.\n\n` +
          `Usuário criado: ${data.role}\n` +
          `Senha temporária: ${data.temp_password}\n\n` +
          `${channelMsg}\n\n` +
          `O candidato precisa trocar a senha no primeiro login.`
        );
        if (data.whatsapp_welcome_link) {
          toast.success("Link de boas-vindas WhatsApp pronto — clique para enviar", {
            duration: 15000,
            action: {
              label: "Abrir WhatsApp",
              onClick: () => window.open(data.whatsapp_welcome_link, "_blank", "noopener"),
            },
          });
        }
      }
      toast.success(`Candidatura ${decision === "approve" ? "aprovada" : "rejeitada"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const changeRole = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}/role`, { role });
      toast.success("Papel atualizado");
      await load();
      // Se virou profissional, dispara modal pra preencher CRM/categorias/valor
      if (role === "profissional") {
        const u = (users || []).find((x) => x.id === id);
        if (u) setMigratingProfessional({ ...u, role: "profissional" });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const remove = async (id, email) => {
    if (!window.confirm(`Excluir ${email}?`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Usuário excluído"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("Usuário criado");
      setForm({ name: "", email: "", password: "", role: "consultor" });
      setCreating(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const toggleBlock = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/${u.blocked ? "unblock" : "block"}`);
      toast.success(u.blocked ? "Usuário desbloqueado" : "Usuário bloqueado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Gerar nova senha temporária para ${u.name} (${u.email})?\nA senha antiga será invalidada.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      // Copia para clipboard e exibe modal com a senha
      try { await navigator.clipboard.writeText(data.temp_password); } catch { /* clipboard blocked in preview */ }
      window.alert(`✅ Nova senha temporária de ${data.name}:\n\n${data.temp_password}\n\n(Copiada para o clipboard)\n\nRepasse por WhatsApp/e-mail. O usuário será forçado a trocar no próximo login.`);
      toast.success("Senha temporária gerada e copiada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao gerar senha"); }
  };

  const saveUserEdit = async () => {
    if (!editingUser) return;
    try {
      const payload = {
        name: editingUser.name, email: editingUser.email,
        phone: editingUser.phone, bio: editingUser.bio,
      };
      if (["consultor", "admin"].includes(editingUser.role)) {
        payload.monthly_commission_goal = editingUser.monthly_commission_goal
          ? Number(editingUser.monthly_commission_goal) : null;
      }
      await api.patch(`/admin/users/${editingUser.id}`, payload);
      toast.success("Usuário atualizado");
      setEditingUser(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const removeClinic = async (id, name) => {
    if (!window.confirm(`Excluir a unidade "${name}"?\nCasos ativos vinculados bloquearão a exclusão.`)) return;
    try {
      await api.delete(`/admin/clinics/${id}`);
      toast.success("Unidade excluída"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  // Normaliza datas do form (YYYY-MM-DD via input type="date") pra ISO
  // completo com fim-do-dia UTC. Sem isso, `_clinic_visible` no backend
  // gera datetime naive e quebra a comparação tz-aware.
  const normalizeDateField = (v) => {
    if (!v) return v;
    const s = String(v).trim();
    if (!s) return null;
    // Já tem hora ou timezone → devolve como está
    if (/[T ]|Z|[+-]\d{2}:?\d{2}$/.test(s)) return s;
    // YYYY-MM-DD puro → assume fim-do-dia UTC
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T23:59:59Z`;
    return s;
  };

  const saveClinicEdit = async () => {
    if (!editingClinic) return;
    try {
      const { id, ...changes } = editingClinic;
      if ("certification_expires_at" in changes) {
        changes.certification_expires_at = normalizeDateField(changes.certification_expires_at);
      }
      if ("certified_at" in changes) {
        changes.certified_at = normalizeDateField(changes.certified_at);
      }
      await api.patch(`/admin/clinics/${id}`, changes);
      toast.success("Unidade atualizada");
      setEditingClinic(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  if (!overview) return <div className="p-10 text-slate-500">Carregando governança…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <WhatsAppLiveNotifier />
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Governança RAF Direct</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1">Administração</h1>
        <p className="text-sm text-slate-600 mt-2">Usuários, perfis, indicadores e auditoria em um só lugar.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <MetricCard icon={Activity} label="Casos totais" value={overview.total_cases} />
        <MetricCard icon={Activity} label="Casos ativos" value={overview.active_cases} accent />
        <MetricCard icon={Building2} label="Unidades Parceiras" value={overview.total_clinics} />
        <MetricCard icon={Shield} label="Entradas de auditoria" value={overview.audit_entries} />
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        {(overview.users_by_role || []).map((r) => (
          <div key={r.role} className="p-4 border border-slate-200 rounded-xl bg-white flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">{ROLE_LABEL[r.role] || r.role}</div>
              <div className="font-display font-bold text-2xl text-[#0B2B62] mt-1">{r.count}</div>
            </div>
            <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-full ${ROLE_COLORS[r.role] || "bg-slate-100 text-slate-700"}`}>{r.role}</span>
          </div>
        ))}
      </div>

      {/* KPI Card: volume de comunicações (últimas 24h + 7d) */}
      <CommKpiCard commKpi={commKpi}
        onViewHistory={() => { load(); setTab("sms"); }}
        onHeatmapCellClick={onHeatmapCell} />

      {/* Widget: status PIX no Stripe (auto-detect + botão manual) */}
      <div className="mb-6"><PixStatusWidget /></div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setTab("users")} data-testid="admin-tab-users"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors ${tab === "users" ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <Users className="w-3.5 h-3.5 inline-block mr-1" /> Usuários
        </button>
        <button onClick={() => setTab("applications")} data-testid="admin-tab-applications"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors ${tab === "applications" ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <Inbox className="w-3.5 h-3.5 inline-block mr-1" /> Candidaturas
          {pendingApplicationsCount > 0 && (
            <span className="ml-1.5 text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full">
              {pendingApplicationsCount}
            </span>
          )}
        </button>
        <button onClick={() => setTab("clinics")} data-testid="admin-tab-clinics"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors ${tab === "clinics" ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <Building2 className="w-3.5 h-3.5 inline-block mr-1" /> Unidades ({clinics.length})
        </button>
        <button onClick={() => setTab("professionals")} data-testid="admin-tab-professionals"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors ${tab === "professionals" ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <UserCog className="w-3.5 h-3.5 inline-block mr-1" /> Profissionais
        </button>
        <button onClick={() => setTab("live")} data-testid="admin-tab-live"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "live" ? "border-red-600 bg-red-600 text-white" : "border-red-200 text-red-600 hover:bg-red-50"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${tab === "live" ? "bg-white" : "bg-red-500"} animate-pulse`} />
          <Radio className="w-3.5 h-3.5" /> Plantão ao vivo
        </button>
        <Link to="/admin/sala-de-guerra" data-testid="admin-war-room-link"
          className="text-sm px-3 py-1.5 rounded-md border border-red-600 bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1">
          🎯 Sala de Guerra
        </Link>
        <Link to="/admin/central-atendimento" data-testid="admin-central-atendimento-link"
          className="text-sm px-3 py-1.5 rounded-md border border-[#0B2B62] bg-[#0B2B62] text-white hover:bg-blue-900 inline-flex items-center gap-1">
          <MessagesSquare className="w-3.5 h-3.5" /> Central de Atendimento
        </Link>
        <Link to="/admin/blog" data-testid="admin-blog-link"
          className="text-sm px-3 py-1.5 rounded-md border border-violet-600 bg-violet-600 text-white hover:bg-violet-700 inline-flex items-center gap-1">
          📰 Blog
        </Link>
        <Link to="/admin/lgpd" data-testid="admin-lgpd-link"
          className="text-sm px-3 py-1.5 rounded-md border border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 inline-flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" /> LGPD
        </Link>
        <Link to="/admin/ajuda" data-testid="admin-ajuda-link"
          className="text-sm px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
          title="Guia completo da plataforma">
          <HelpCircle className="w-3.5 h-3.5" /> Ajuda
        </Link>
        <button onClick={() => setTab("banned")} data-testid="admin-tab-banned"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "banned" ? "border-red-600 bg-red-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <ShieldOff className="w-3.5 h-3.5" /> Banidos & Excluídos
        </button>
        <button onClick={() => setTab("analytics-live")} data-testid="admin-tab-analytics-live"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "analytics-live" ? "border-red-600 bg-red-600 text-white" : "border-red-200 text-red-600 hover:bg-red-50"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${tab === "analytics-live" ? "bg-white" : "bg-red-500"} animate-pulse`} />
          <Eye className="w-3.5 h-3.5" /> Ao vivo no site
        </button>
        <button onClick={() => setTab("analytics")} data-testid="admin-tab-analytics"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "analytics" ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <BarChart3 className="w-3.5 h-3.5" /> Cert Analytics
        </button>
        <button onClick={() => setTab("courtesies")} data-testid="admin-tab-courtesies"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "courtesies" ? "border-amber-600 bg-amber-600 text-white" : "border-amber-200 text-amber-700 hover:bg-amber-50"}`}>
          <Gift className="w-3.5 h-3.5" /> Cortesias
        </button>
        <button onClick={() => setTab("alertas")} data-testid="admin-tab-alertas"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "alertas" ? "border-red-600 bg-red-600 text-white" : "border-red-200 text-red-700 hover:bg-red-50"}`}>
          <Siren className="w-3.5 h-3.5" /> Alertas 24h
        </button>
        <button onClick={() => setTab("financeiro")} data-testid="admin-tab-financeiro"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "financeiro" ? "border-emerald-600 bg-emerald-700 text-white" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
          <DollarSign className="w-3.5 h-3.5" /> Financeiro
        </button>
        <button onClick={() => setTab("reaberturas")} data-testid="admin-tab-reaberturas"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "reaberturas" ? "border-amber-600 bg-amber-600 text-white" : "border-amber-200 text-amber-700 hover:bg-amber-50"}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Reaberturas SLA
        </button>
        <button onClick={() => setTab("cancelados")} data-testid="admin-tab-cancelados"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "cancelados" ? "border-slate-800 bg-slate-800 text-white" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
          🚫 Cancelados
        </button>
        <button onClick={() => setTab("cupons")} data-testid="admin-tab-cupons"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "cupons" ? "border-amber-500 bg-amber-500 text-white" : "border-amber-200 text-amber-700 hover:bg-amber-50"}`}>
          🎟 Cupons FP
        </button>
        <button onClick={() => setTab("convenios")} data-testid="admin-tab-convenios"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "convenios" ? "border-emerald-600 bg-emerald-600 text-white" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
          🩺 Convênios · Consultas
        </button>
        <button onClick={() => setTab("sms")} data-testid="admin-tab-sms"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "sms" ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <Send className="w-3.5 h-3.5" /> Comunicação Twilio
        </button>
        <button onClick={() => setTab("comm-health")} data-testid="admin-tab-comm-health"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "comm-health" ? "border-emerald-600 bg-emerald-700 text-white" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
          <Heart className="w-3.5 h-3.5" /> Saúde Twilio
        </button>
        <button onClick={() => setTab("reviews")} data-testid="admin-tab-reviews"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "reviews" ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          ★ Avaliações
        </button>
        <button onClick={() => setTab("divulgacao")} data-testid="admin-tab-divulgacao"
          className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${tab === "divulgacao" ? "border-fuchsia-600 bg-fuchsia-600 text-white" : "border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50"}`}>
          📣 Divulgação
        </button>
        <Link to="/consultor/auditoria" data-testid="admin-goto-audit" className="text-sm px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50">Auditoria completa</Link>
        <Link to="/consultor/motivos" data-testid="admin-goto-reasons" className="text-sm px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50">Motivos de encerramento</Link>
        <Link to="/consultor" data-testid="admin-goto-consultor" className="text-sm px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50">Painel operacional</Link>
        <button data-testid="admin-reset-cases" onClick={resetCases}
          className="text-sm px-3 py-1.5 border border-red-300 text-red-600 rounded-md hover:bg-red-50 inline-flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> Limpar dados de teste
        </button>
        {tab === "users" && (
          <button data-testid="admin-new-user" onClick={() => setCreating(!creating)}
            className="ml-auto text-sm px-3 py-1.5 bg-[#0B2B62] text-white rounded-md hover:bg-slate-800 inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Novo usuário
          </button>
        )}
      </div>

      {tab === "applications" && (
        <CandidaturasList applications={applications} onDecide={decide} />
      )}

      {tab === "analytics-live" && <AnalyticsLivePanel />}
      {tab === "banned" && <BannedUsersPanel />}

      {tab === "users" && creating && (
        <form onSubmit={create} className="mb-6 p-4 border border-[#0B2B62] rounded-xl bg-slate-50 grid md:grid-cols-4 gap-3" data-testid="admin-create-form">
          <input required placeholder="Nome" data-testid="admin-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <input required type="email" placeholder="E-mail" data-testid="admin-new-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <input required type="password" placeholder="Senha (min. 6)" minLength={6} data-testid="admin-new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <div className="flex gap-2">
            <select data-testid="admin-new-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
              <option value="familia">Família</option>
              <option value="consultor">Consultor</option>
              <option value="clinica">Unidade Parceira</option>
              <option value="profissional">Profissional</option>
              <option value="admin">Admin</option>
            </select>
            <button type="submit" data-testid="admin-create-submit" className="px-3 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold">Criar</button>
          </div>
        </form>
      )}

      {tab === "users" && (
        <UsersTable
          users={users}
          onChangeRole={changeRole}
          onEdit={setEditingUser}
          onToggleBlock={toggleBlock}
          onDelete={remove}
          onResetPassword={resetPassword}
        />
      )}
      {tab === "clinics" && (
        <ClinicsTable clinics={clinics} onEdit={setEditingClinic} onRemove={removeClinic} onRefresh={load} />
      )}
      {tab === "professionals" && (
        <ProfessionalsTable />
      )}
      {tab === "reviews" && (
        <ReviewsPanel />
      )}
      {tab === "comm-health" && (
        <CommHealthPanel />
      )}

      {tab === "live" && (
        <LivePlantaoPanel
          live={live}
          onRefresh={loadLive}
          kickOnCall={kickOnCall}
          bulkLive={bulkLive}
          setBulkLive={setBulkLive}
        />
      )}

      {tab === "sms" && (
        <CommsPanel commHistory={commHistory} onReloadHistory={loadCommHistory}
          heatmapFilter={heatmapFilter}
          onClearHeatmapFilter={() => setHeatmapFilter(null)} />
      )}

      {tab === "analytics" && (
        <CertAnalyticsPanel certAnalytics={certAnalytics} onReload={loadCertAnalytics} />
      )}

      {tab === "courtesies" && (
        <CourtesiesPanel />
      )}

      {tab === "reaberturas" && (
        <ReopenedCasesPanel />
      )}

      {tab === "cancelados" && (
        <CancelledCasesPanel />
      )}

      {tab === "cupons" && (
        <AdminCouponsPanel />
      )}

      {tab === "convenios" && (
        <AdminConveniosPanel />
      )}

      {tab === "financeiro" && (
        <AdminFinancePanel />
      )}

      {tab === "alertas" && (
        <ActiveAlertsPanel />
      )}

      {tab === "divulgacao" && (
        <div className="space-y-6">
          <TemplatesHealthPanel />
          <OutreachCampaign />
        </div>
      )}

      {migratingProfessional && (
        <ProfessionalProfileModal
          user={migratingProfessional}
          onClose={() => setMigratingProfessional(null)}
          onSaved={() => { setMigratingProfessional(null); load(); }}
        />
      )}

      {editingUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditingUser(null)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
            <h3 className="font-display font-bold text-xl text-[#0B2B62] mb-4">Editar usuário</h3>
            <div className="space-y-3">
              <div><label className="text-xs text-slate-500 uppercase">Nome</label><input value={editingUser.name || ""} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="edit-user-name" /></div>
              <div>
                <label className="text-xs text-slate-500 uppercase">E-mail de acesso</label>
                <input type="email" value={editingUser.email || ""} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="edit-user-email" />
                <p className="text-[10px] text-amber-700 mt-1">Alterar este e-mail muda o login do usuário. Confirme com ele antes de salvar.</p>
              </div>
              <div><label className="text-xs text-slate-500 uppercase">Telefone</label><input value={editingUser.phone || ""} onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="edit-user-phone" /></div>
              <div><label className="text-xs text-slate-500 uppercase">Bio</label><textarea rows={3} value={editingUser.bio || ""} onChange={(e) => setEditingUser({ ...editingUser, bio: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></div>
              {["consultor", "admin"].includes(editingUser.role) && (
                <div>
                  <label className="text-xs text-slate-500 uppercase">Meta mensal de comissão (R$)</label>
                  <input type="number" min="0" step="100"
                    value={editingUser.monthly_commission_goal ?? ""}
                    onChange={(e) => setEditingUser({ ...editingUser, monthly_commission_goal: e.target.value })}
                    placeholder="Ex: 5000 (deixe vazio para sem meta)"
                    className="w-full px-3 py-2 border border-emerald-300 rounded-md text-sm bg-emerald-50/50"
                    data-testid="edit-user-goal" />
                  <p className="text-[11px] text-slate-500 mt-1">Aparece como barra de progresso no widget "Minhas comissões" do consultor.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditingUser(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Cancelar</button>
              <button onClick={saveUserEdit} className="px-4 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold" data-testid="edit-user-save">Salvar</button>
            </div>
          </div>
        </div>
      )}

      {editingClinic && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditingClinic(null)}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-clinic-modal">
            <h3 className="font-display font-bold text-xl text-[#0B2B62] mb-4">Editar Unidade</h3>
            <div className="space-y-3">
              {["name","city","state","phone","email","address","description","certification_number"].map((f) => (
                <div key={f}>
                  <label className="text-xs text-slate-500 uppercase">{f === "certification_number" ? "Cert. Nº (RAF Direct)" : f}</label>
                  {f === "description" ? (
                    <textarea rows={3} value={editingClinic[f] || ""} onChange={(e) => setEditingClinic({ ...editingClinic, [f]: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid={`edit-clinic-${f}`} />
                  ) : (
                    <input value={editingClinic[f] || ""} onChange={(e) => setEditingClinic({ ...editingClinic, [f]: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid={`edit-clinic-${f}`} />
                  )}
                </div>
              ))}
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs text-slate-500 uppercase">Certificada em</label>
                  <input type="date" value={(editingClinic.certified_at || "").slice(0,10)} onChange={(e) => setEditingClinic({ ...editingClinic, certified_at: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="edit-clinic-certified_at" />
                </div>
                <div><label className="text-xs text-slate-500 uppercase">Válida até</label>
                  <input type="date" value={(editingClinic.certification_expires_at || "").slice(0,10)} onChange={(e) => setEditingClinic({ ...editingClinic, certification_expires_at: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="edit-clinic-cert-expires" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!editingClinic.is_certified} onChange={(e) => setEditingClinic({ ...editingClinic, is_certified: e.target.checked })} data-testid="edit-clinic-certified" />
                <span>Exibir selo <b>Credenciada RAF Direct</b> no perfil público</span>
              </label>
              <div><label className="text-xs text-slate-500 uppercase">Vagas</label><input type="number" value={editingClinic.beds_available || 0} onChange={(e) => setEditingClinic({ ...editingClinic, beds_available: parseInt(e.target.value || 0) })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditingClinic(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Cancelar</button>
              <button onClick={saveClinicEdit} className="px-4 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold" data-testid="edit-clinic-save">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, accent }) => (
  <div className={`p-4 border rounded-xl ${accent ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 bg-white"}`}>
    <div className="flex items-center justify-between">
      <div className={`text-xs uppercase tracking-widest ${accent ? "text-white/70" : "text-slate-500"}`}>{label}</div>
      <Icon className={`w-4 h-4 ${accent ? "text-white/70" : "text-slate-400"}`} strokeWidth={1.8} />
    </div>
    <div className={`font-display font-bold text-3xl mt-2 ${accent ? "text-white" : "text-[#0B2B62]"}`}>{value}</div>
  </div>
);

export default AdminDashboard;
